import { useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { isEqual } from 'lodash'
import { Dropdown, DropdownMenu, DropdownOption, DropdownSeparator } from 'datocms-react-ui'
import { RenderFieldExtensionCtx } from 'datocms-plugin-sdk'
import { FaBold, FaImage, FaItalic, FaMinusCircle, FaPlusCircle } from 'react-icons/fa'
import { FaEllipsisVertical, FaTextSlash } from 'react-icons/fa6'
import { plainToStructureText, structuredTextToPlain } from 'src/utils/dast'
import s from './styles.module.css'
import { CellType as CellTypeEnum, Cell as CellType, EditingCell, TableOptions } from 'src/types.d'

export const initialCellValue = (): CellType => ({
  id: uuidv4(),
  type: CellTypeEnum.StructuredText,
  value: plainToStructureText({ value: '' }),
})

const toInput = (value: CellType): EditingCell => {
  if (value.type === CellTypeEnum.Image) {
    return { ...value, value: value.value }
  }

  const stringValue = JSON.stringify(value.value)
  return { ...value, isBold: stringValue.includes('strong'), isItalic: stringValue.includes('emphasis'), value: structuredTextToPlain(value.value) }
}

const toOutput = (value: EditingCell): CellType => {
  if (value.type === CellTypeEnum.Image) {
    return { ...value, value: value.value }
  }

  return { ...value, value: plainToStructureText(value) }
}

type OwnProps = {
  ctx: RenderFieldExtensionCtx
  options: TableOptions
  value: CellType
  columnIndex: number
  rowIndex: number
  isTopRow?: boolean
  onUpdate: (value: CellType) => void
  onUpdateOptions: (options: TableOptions) => void
  onAddColumn: () => void
  onAddRow: () => void
  onRemoveColumn: () => void
}

export default function Cell(props: OwnProps) {
  const [data, setData] = useState<EditingCell>(toInput(props.value))
  const [isFocused, setIsFocused] = useState(false)
  const [isMouseHover, setIsMouseHover] = useState(false)

  useEffect(() => {
    const outputData = toOutput(data)
    if (!isEqual(outputData, props.value)) {
      props.onUpdate(outputData)
    }
  }, [props, data])

  const onUpdate = (partialData: Partial<EditingCell>) => {
    setData({ ...data, ...partialData } as EditingCell)
  }

  const selectUpload = async () => {
    try {
      const upload = await props.ctx.selectUpload({ multiple: false })
      if (upload) onUpdate({ type: CellTypeEnum.Image, value: upload })
    } catch (error: any) {
      props.ctx.alert(error.message)
    }
  }

  return (
    <div
      className={s.cell}
      onMouseEnter={() => setIsMouseHover(true)}
      onMouseLeave={() => setIsMouseHover(false)}
      style={{ backgroundColor: (props.columnIndex === props.options.mainColumn || props.rowIndex === props.options.mainRow) ? 'var(--light-color)' : 'inherit' }}>
      <div className={s.actions} style={{ display: isMouseHover || isFocused ? 'flex' : 'none' }}>
        <Dropdown
          renderTrigger={({ onClick }) => {
            return (
              <button className={s.actionsTrigger} onClick={onClick} tabIndex={-1}>
                <FaEllipsisVertical style={{
                  position: 'absolute',
                  top: '2px',
                  left: 0,
                }} />
              </button>
            )
          }}
        >
          <DropdownMenu alignment={props.columnIndex === 0 ? "left" : "right"}>
            {data.type === CellTypeEnum.StructuredText && <>
              <div className={s.font}>
                <button className={s.fontAction} onClick={() => onUpdate({ isBold: false, isItalic: false })}>
                  <FaTextSlash />
                </button>
                <button className={[s.fontAction, data.isBold && 'active'].join(' ')} onClick={() => onUpdate({ isBold: !data.isBold })}>
                  <FaBold />
                </button>
                <button className={[s.fontAction, data.isItalic && 'active'].join(' ')} onClick={() => onUpdate({ isItalic: !data.isItalic })}>
                  <FaItalic />
                </button>
                <button className={s.fontAction} onClick={() => selectUpload()}>
                  <FaImage />
                </button>
              </div>
            </>}
            {data.type === CellTypeEnum.Image && <DropdownOption onClick={() => onUpdate({ type: CellTypeEnum.StructuredText, value: '' })}>
              <FaMinusCircle size={24} style={{ fill: 'red' }} />&nbsp;<span>Remove image</span>
            </DropdownOption>}
            <DropdownSeparator />
            {props.isTopRow
              ? props.columnIndex === props.options.mainColumn
                ? <DropdownOption onClick={() => props.onUpdateOptions({ ...props.options, mainColumn: -1 })}>Remove as <strong>main</strong> column</DropdownOption>
                : <DropdownOption onClick={() => props.onUpdateOptions({ ...props.options, mainColumn: props.columnIndex })}>Define as <strong>main</strong> column</DropdownOption>
              : null}
            {props.rowIndex === props.options.mainRow
              ? <DropdownOption onClick={() => props.onUpdateOptions({ ...props.options, mainRow: -1 })}>Remove as <strong>main</strong> row</DropdownOption>
              : <DropdownOption onClick={() => props.onUpdateOptions({ ...props.options, mainRow: props.rowIndex })}>Define as <strong>main</strong> row</DropdownOption>}
          </DropdownMenu>
        </Dropdown>
      </div>

      {data.type === CellTypeEnum.Image
        ? <img src={data.value.attributes.url} alt="" className={s.image} />
        : <textarea
          className={s.textarea}
          value={data.value}
          rows={3}
          onChange={(e) => onUpdate({ value: e.target.value })}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          style={{
            fontWeight: data.isBold ? 'bold' : 'normal',
            fontStyle: data.isItalic ? 'italic' : 'normal',
          }}
        />}

      {props.isTopRow && <FaMinusCircle className={s.removeColumn} onClick={props.onRemoveColumn} style={{ display: isMouseHover ? 'block' : 'none' }} />}
      <FaPlusCircle className={s.addColumn} onClick={props.onAddColumn} style={{ display: isMouseHover ? 'block' : 'none' }} />
      <FaPlusCircle className={s.addRow} onClick={props.onAddRow} style={{ display: isMouseHover ? 'block' : 'none' }} />
    </div>
  )
}
